import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SystemIcon from '../ShortStatus/SystemIcon';
import Button from '../../../components/CustomButtons/ButtonTS';
import CancelIcon from '@material-ui/icons/Cancel';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeaderTS';
import CardIcon from '../../../components/Card/CardIcon';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { closeSweetAlertStyle } from '../../helpers/CustomStyles';
import { INode } from '../../helpers/interfaces';
import utils from '../../helpers/GlobalVariables';
import { deleteNode, closeDeleteNodeNotification } from '../../stores/actions/installerActions';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Loading from '../Loading/Loading';

interface RemoveNodeConfirmationProps {
	node: INode;
	deleteNodeSuccessfully: boolean;
	deleteNodeFailed: boolean;
	mobileView: boolean;
	hideRemoveNodeConfirmation: () => void;
	deleteNode: (nodeId: number) => void;
	closeDeleteNodeNotification: () => void;
}

const closeButtonStyle = {
	border: '1px solid #00acc1',
};

const RemoveNodeConfirmationCard = ({
	node,
	deleteNodeSuccessfully,
	deleteNodeFailed,
	mobileView,
	closeDeleteNodeNotification,
	hideRemoveNodeConfirmation,
	deleteNode,
}: RemoveNodeConfirmationProps) => {
	const [popupTimeoutEnabled, setPopupTimeoutEnabled] = useState(false);
	const [isLoaderRender, setIsLoaderRender] = useState(false);

	useEffect(() => {
		closeDeleteNodeNotification();
	}, [closeDeleteNodeNotification]);

	useEffect(() => {
		if (deleteNodeSuccessfully) {
			setPopupTimeoutEnabled(true);
		}
		if (deleteNodeSuccessfully || deleteNodeFailed) {
			const timeoutNotification = setTimeout(() => {
				closeDeleteNodeNotification();
			}, 6000);
			setIsLoaderRender(false);
			return () => {
				clearTimeout(timeoutNotification);
			};
		}
	}, [closeDeleteNodeNotification, deleteNodeSuccessfully, deleteNodeFailed]);

	useEffect(() => {
		if (popupTimeoutEnabled) {
			const closeWindowTimeout = setTimeout(() => {
				setPopupTimeoutEnabled(false);
				hideRemoveNodeConfirmation();
			}, 7500);
			return () => {
				clearTimeout(closeWindowTimeout);
			};
		}
	}, [hideRemoveNodeConfirmation, popupTimeoutEnabled]);

	const closeButtonHover = (e) => {
		const button = e.target;
		button.style.backgroundColor = 'rgba(0, 172, 193, 0.1)';
	};

	const closeButtonLeave = (e) => {
		const button = e.target;
		button.style.backgroundColor = '#ffffff';
	};

	const handleDeleteNode = () => {
		deleteNode(node.id, node.systemId);
	};

	const handleCloseNotification = () => {
		setIsLoaderRender(false);
		closeDeleteNodeNotification();
	};

	const renderDesktopNotifications = (): JSX.Element => {
		if (mobileView) return null;
		if (!deleteNodeSuccessfully && !deleteNodeFailed) return null;

		const color = deleteNodeSuccessfully ? 'success' : 'danger';
		const message = deleteNodeSuccessfully
			? 'Node was deleted successfully.'
			: 'You do not have the permission to remove the node.';
		const isOpen = deleteNodeSuccessfully || deleteNodeFailed ? true : false;
		return (
			<Snackbar
				close={true}
				closeNotification={handleCloseNotification}
				color={color}
				message={message}
				open={isOpen}
				place='tc'
			/>
		);
	};

	const renderMobileNotifications = () => {
		const headerStyle = utils.getHeaderStyle(deleteNodeFailed);
		if (!mobileView) return null;
		if (deleteNodeFailed || deleteNodeSuccessfully) {
			return (
				<GridItem xs={12}>
					<h5
						className={deleteNodeFailed ? 'notification-open-create-system-fail' : 'notification-open-create-system-success'}
						style={headerStyle}
					>
						{deleteNodeFailed ? 'You do not have the permission to remove the node.' : 'Node was deleted successfully.'}
					</h5>
				</GridItem>
			);
		}
	};

	const handleRemoveButtonClicked = () => {
		setIsLoaderRender(true);
		handleDeleteNode();
	};

	return (
		<>
			<div>
				<GridContainer>
					<GridItem xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							data-testid='cancelButton'
							simple
							justIcon
							color='info'
							style={closeSweetAlertStyle}
							onClick={hideRemoveNodeConfirmation}
						>
							<CancelIcon style={{ width: '20px', height: '20px' }} />
						</Button>
					</GridItem>
				</GridContainer>
				<Card chart style={{ overflow: 'visible', marginTop: '10px', marginBottom: '0' }} className='remove-node-card'>
					<CardHeader color='info' icon>
						<CardIcon color='info'>
							<SystemIcon />
						</CardIcon>
						<h3 style={utils.sweetAlertHeaderStyle} className='remove-node-header'>
							{`${node.name}`}
						</h3>
					</CardHeader>
					<CardBody>
						<GridContainer>
							<GridItem xs={12}>
								<h5>This operation is irreversible. Are you sure to delete this node?</h5>
							</GridItem>
							{renderMobileNotifications()}
							<GridItem xs={12} style={{ margin: '15px 0 0 0 ' }}>
								<div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
									<Button
										className='deleteNodeCloseButton'
										simple
										color='info'
										style={closeButtonStyle}
										onClick={hideRemoveNodeConfirmation}
										onMouseEnter={(e) => closeButtonHover(e)}
										onMouseLeave={(e) => closeButtonLeave(e)}
									>
										cancel
									</Button>
									<Button
										style={{ backgroundColor: isLoaderRender ? '#ccc' : '#f44336' }}
										onClick={handleRemoveButtonClicked}
										disabled={isLoaderRender || deleteNodeSuccessfully || deleteNodeFailed}
									>
										{isLoaderRender ? <Loading transform={1} height={17} width={48} color='darkGray' /> : 'remove'}
									</Button>
								</div>
							</GridItem>
						</GridContainer>
					</CardBody>
				</Card>
				{renderDesktopNotifications()}
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		nodes: state.dashboard.nodes,
		deleteNodeSuccessfully: state.installer.deleteNodeSuccessfully,
		deleteNodeFailed: state.installer.deleteNodeFailed,
		mobileView: state.dashboard.mobileView,
	};
};

const actions = {
	deleteNode,
	closeDeleteNodeNotification,
};

export default connect(mapStateToProps, actions)(RemoveNodeConfirmationCard);
